import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";

import { usePublicTypesByIds } from "../catalog/api";
import { formatDollarsRound } from "../catalog/pricing";
import type { TypeSummary } from "../catalog/types";

import { useCart, useRemoveItem, useSetCartWaiver, useUpdateItem } from "./hooks";
import { PromoEntry } from "./PromoEntry";
import type { Cart, CartItem } from "./types";

const OPERATOR_HOURS_DEBOUNCE_MS = 300;

// 2c-cart-and-checkout FR-3. The cart page lists each item the
// customer has added, with operator-hours / waiver toggles inline,
// plus a remove action. Quantities > 1 are stored as separate items
// (one rental_assignment per row), so this page renders one card
// per line.
//
// v1 gaps documented for the follow-up checkout slice:
//   - FR-3's "Quick edit of attachments via inline shadcn/ui Sheet"
//     is not yet here — the cart shows a count of selected
//     attachments only; changes require remove + re-add.
//   - The price-breakdown sidebar is intentionally minimal; the
//     full breakdown (subtotal, tax, surcharge, total) lives in
//     the checkout flow per FR-12.
export function CartPage(): JSX.Element {
  const { data: cart, isPending, isError } = useCart();

  if (isPending) return <CartSkeleton />;
  if (isError || !cart) {
    return (
      <Alert variant="destructive">
        We couldn&rsquo;t load your cart. Try refreshing the page.
      </Alert>
    );
  }
  if (cart.payload.items.length === 0) return <EmptyCart />;

  return <CartBody cart={cart} />;
}

function CartBody({ cart }: { cart: Cart }): JSX.Element {
  const typeIds = useMemo(
    () => Array.from(new Set(cart.payload.items.map((i) => i.type_id))),
    [cart.payload.items]
  );
  const { data: types = [] } = usePublicTypesByIds(typeIds);
  const typesById = useMemo(
    () => new Map(types.map((t) => [t.id, t])),
    [types]
  );

  return (
    <div className="pb-24 md:pb-0">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-brand-black md:text-3xl">
          Your cart
        </h1>
        <p className="mt-1 text-sm text-zinc-600">
          {cart.payload.items.length}{" "}
          {cart.payload.items.length === 1 ? "item" : "items"}. Review and
          continue to checkout to schedule delivery.
        </p>
      </header>

      <div className="grid gap-6 md:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <InsuranceStep cart={cart} />
          <ul className="space-y-4">
            {cart.payload.items.map((item, index) => (
              <li key={`${item.type_id}-${index}`}>
                <CartLine
                  index={index}
                  item={item}
                  type={typesById.get(item.type_id)}
                />
              </li>
            ))}
            <li>
              <AddAnotherItemLink />
            </li>
          </ul>
        </div>

        <CartSidebar cart={cart} />
      </div>
    </div>
  );
}

function CartLine({
  index,
  item,
  type
}: {
  index: number;
  item: CartItem;
  type: TypeSummary | undefined;
}): JSX.Element {
  const removeItem = useRemoveItem();
  // The type may still be loading; render skeletons for the title +
  // price line so the row's height doesn't shift when the lookup
  // lands. The image always reserves a 16×16 square below.
  const title = type?.name ?? <Skeleton className="h-4 w-32" />;
  // While the remove mutation is in flight the row is destined to
  // disappear — disabling all inputs prevents a late-arriving PATCH
  // (the operator_hours debounce in particular) from racing with the
  // DELETE and landing on the wrong index.
  const rowDisabled = removeItem.isPending;

  return (
    <article
      className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm"
      aria-busy={rowDisabled}
    >
      <div className="flex items-start gap-4">
        {type?.featured_image_url ? (
          <img
            src={type.featured_image_url}
            alt=""
            className="h-16 w-16 flex-none rounded-md object-cover"
          />
        ) : (
          <div className="h-16 w-16 flex-none rounded-md bg-zinc-100" />
        )}
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-brand-black">{title}</h2>
          {type ? (
            type.from_price_per_week_cents ? (
              <p className="mt-0.5 text-sm text-zinc-600">
                From {formatDollarsRound(type.from_price_per_week_cents)}/week
              </p>
            ) : (
              // Reserve the price-line vertical space even when the type
              // exposes no `from_price_per_week_cents` so the controls
              // below don't shift up. Matches the loading-skeleton height.
              <div className="mt-1 h-3" aria-hidden="true" />
            )
          ) : (
            <Skeleton className="mt-1 h-3 w-24" />
          )}
          {/*
            Phase 4c COI waiver bypass — the per-item damage waiver
            checkbox is replaced by the cart-level chooser rendered by
            <InsuranceStep />. We keep the operator-hours input here.
          */}
          <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
            <DebouncedOperatorHoursInput
              index={index}
              value={item.operator_hours}
              disabled={rowDisabled}
            />
          </div>
          {item.attachment_type_ids.length > 0 ? (
            <p className="mt-2 text-xs text-zinc-500">
              {item.attachment_type_ids.length}{" "}
              {item.attachment_type_ids.length === 1 ? "attachment" : "attachments"}{" "}
              selected
            </p>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Remove ${type?.name ?? "item"} from cart`}
          onClick={() => removeItem.mutate(index)}
          disabled={rowDisabled}
        >
          Remove
        </Button>
      </div>
    </article>
  );
}

// Debounces the operator_hours PATCH so a customer typing "12"
// doesn't fire two mutations against an index-addressed endpoint —
// the second would otherwise race with any subsequent DELETE and
// could land after the row was removed, mutating a sibling.
//
// State machine:
//   - `local` is the input's display value; updates on every keystroke.
//   - `lastSent` is the value we promised to send (advanced when the
//     debounced timer fires, not when the server confirms). This
//     prevents the infinite-retry loop that would happen if we kept
//     `lastSent` stale across renders: react-query returns a new
//     `mutate` reference each render, so a deps-array tied to it
//     would re-fire the effect and re-schedule the timer.
//   - On mutation failure we capture the error in `latchedError` (a
//     useState, not the mutation's transient `isError`) so it stays
//     visible across the next keystroke and only clears when a
//     subsequent PATCH succeeds.
//   - `disabled` cancels any pending timer (via the deps array +
//     cleanup) AND short-circuits scheduling — so clicking Remove
//     mid-edit can't leave a PATCH queued behind the DELETE.
function DebouncedOperatorHoursInput({
  index,
  value,
  disabled
}: {
  index: number;
  value: number;
  disabled: boolean;
}): JSX.Element {
  const updateItem = useUpdateItem();
  // Hold a ref to the mutate fn so the debounce effect doesn't list
  // it as a dep (react-query rebuilds the mutation object on every
  // render — including it would re-fire the effect every render and
  // reschedule the timer in a loop on a slow / failing network).
  const mutateRef = useRef(updateItem.mutate);
  useEffect(() => {
    mutateRef.current = updateItem.mutate;
  });

  const [local, setLocal] = useState<number>(value);
  const lastSent = useRef<number>(value);
  const [latchedError, setLatchedError] = useState<string | null>(null);

  // Sync from props when the cart re-fetches with a different value
  // (e.g. another tab edited).
  useEffect(() => {
    setLocal(value);
    lastSent.current = value;
  }, [value]);

  useEffect(() => {
    if (disabled) return undefined;
    if (local === lastSent.current) return undefined;
    const target = local;
    const handle = setTimeout(() => {
      lastSent.current = target;
      mutateRef.current(
        { index, patch: { operator_hours: target } },
        {
          onSuccess: () => setLatchedError(null),
          onError: () => {
            // Roll lastSent back so the next keystroke re-arms the
            // debounce instead of treating the failed value as synced.
            lastSent.current = value;
            setLatchedError("Couldn’t save — try again");
          }
        }
      );
    }, OPERATOR_HOURS_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [local, index, disabled, value]);

  return (
    <label className="flex items-center gap-2">
      <span>Operator hours</span>
      <input
        type="number"
        min={0}
        max={40}
        disabled={disabled}
        value={local}
        onChange={(e) => setLocal(Number(e.currentTarget.value) || 0)}
        className="w-16 rounded-md border border-zinc-300 px-2 py-1 text-sm"
        aria-invalid={latchedError ? true : undefined}
        aria-describedby={latchedError ? `operator-hours-error-${index}` : undefined}
      />
      {latchedError ? (
        <span
          id={`operator-hours-error-${index}`}
          role="alert"
          className="text-xs text-brand-red"
        >
          {latchedError}
        </span>
      ) : null}
    </label>
  );
}

function AddAnotherItemLink(): JSX.Element {
  return (
    <Link
      to="/"
      className="block rounded-lg border border-dashed border-zinc-300 p-4 text-center text-sm text-zinc-600 hover:border-brand-red hover:text-brand-red"
    >
      + Add another item from the catalog
    </Link>
  );
}

// Phase 4c COI waiver bypass — FR-2. Cart-level insurance chooser.
// Hidden when no line on the cart requires COI (server tells us via
// `cart.any_line_requires_coi`); otherwise renders a two-option radio
// that defaults to "Damage waiver". Toggling fires PATCH
// /api/v1/cart/insurance.json and the cart quote re-runs against the
// new flag value.
function InsuranceStep({ cart }: { cart: Cart }): JSX.Element | null {
  const setWaiver = useSetCartWaiver();

  if (!cart.any_line_requires_coi) return null;

  const isWaiver = cart.damage_waiver_opted_in;
  const disabled = setWaiver.isPending;

  return (
    <section
      aria-labelledby="cart-insurance-heading"
      className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm"
    >
      <h2 id="cart-insurance-heading" className="text-base font-semibold text-brand-black">
        Insurance &amp; liability
      </h2>
      <p className="mt-1 text-sm text-zinc-600">
        How will you cover liability for this rental?
      </p>

      <div className="mt-3 space-y-2">
        <label className="flex items-start gap-3 rounded-md border border-zinc-200 p-3 hover:border-zinc-300">
          <input
            type="radio"
            name="cart-insurance"
            checked={isWaiver}
            disabled={disabled}
            onChange={() => setWaiver.mutate(true)}
            data-testid="insurance-waiver"
            className="mt-1 h-4 w-4 text-brand-red"
          />
          <span className="text-sm">
            <span className="font-semibold text-brand-black">
              Add JDR&rsquo;s damage waiver
            </span>
            <span className="ml-2 text-zinc-600">— recommended, no paperwork.</span>
          </span>
        </label>

        <label className="flex items-start gap-3 rounded-md border border-zinc-200 p-3 hover:border-zinc-300">
          <input
            type="radio"
            name="cart-insurance"
            checked={!isWaiver}
            disabled={disabled}
            onChange={() => setWaiver.mutate(false)}
            data-testid="insurance-coi"
            className="mt-1 h-4 w-4 text-brand-red"
          />
          <span className="text-sm">
            <span className="font-semibold text-brand-black">
              I&rsquo;ll provide a Certificate of Insurance
            </span>
            <span className="ml-2 text-zinc-600">
              — free. Upload from your account after checkout. Dispatch
              can&rsquo;t release the unit until JDR approves your COI.
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}

function CartSidebar({ cart }: { cart: Cart }): JSX.Element {
  const navigate = useNavigate();
  const itemsCount = cart.payload.items.length;
  return (
    <aside className="md:sticky md:top-20 md:self-start">
      <div className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-brand-black">Order summary</h2>
        <p className="mt-2 text-sm text-zinc-600">
          A full breakdown — taxes, delivery, and card surcharge — appears at
          checkout once you&rsquo;ve picked a delivery address and date.
        </p>
        {/* Phase 4a promo-codes FR-9 — apply / remove a promo code on
            the in-progress cart. The discount surfaces in the
            price-breakdown at checkout. */}
        <div className="mt-4">
          <PromoEntry appliedCode={cart.applied_promo?.code ?? null} />
        </div>
        <Button
          className="mt-4 w-full"
          size="lg"
          disabled={itemsCount === 0}
          onClick={() => navigate("/checkout")}
        >
          Continue to checkout
        </Button>
      </div>
    </aside>
  );
}

function EmptyCart(): JSX.Element {
  return (
    <section className="rounded-lg border border-dashed border-zinc-300 p-10 text-center">
      <h1 className="text-xl font-semibold text-brand-black">Your cart is empty</h1>
      <p className="mt-2 text-sm text-zinc-600">
        Browse the catalog to add equipment to your cart.
      </p>
      <Link
        to="/"
        className="mt-4 inline-block rounded-md bg-brand-red px-4 py-2 text-sm font-medium text-brand-white hover:bg-brand-red/90"
      >
        Browse equipment
      </Link>
    </section>
  );
}

function CartSkeleton(): JSX.Element {
  return (
    <div className="space-y-4" aria-busy="true">
      <Skeleton className="h-6 w-32" />
      <div className="grid gap-6 md:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
        <Skeleton className="h-40 w-full" />
      </div>
    </div>
  );
}
